/**
 * useCreateDeploymentToken Hook
 *
 * React Query mutation for creating a new deployment token.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentTokenService } from '../../../api/deployment-token.service';
import type {
  DeploymentTokenCreateDTO,
  DeploymentTokenCreateResponse,
} from '../../../types/deployment-token.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateDeploymentTokenOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentTokenCreateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for creating a new deployment token
 */
export const useCreateDeploymentToken = (
  options: UseCreateDeploymentTokenOptions
): UseMutationResult<DeploymentTokenCreateResponse, Error, DeploymentTokenCreateDTO> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentTokenCreateResponse, Error, DeploymentTokenCreateDTO>({
    mutationFn: async (data: DeploymentTokenCreateDTO) => {
      console.log('[useCreateDeploymentToken] Creating deployment token:', data.tokenCode);
      return await deploymentTokenService.createDeploymentToken(tenantId, applicationId, data);
    },
    onSuccess: (data) => {
      console.log('[useCreateDeploymentToken] Deployment token created successfully:', data.tokenId);
      // Invalidate deployment tokens list query
      queryClient.invalidateQueries({ queryKey: ['deployment-tokens', tenantId, applicationId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useCreateDeploymentToken] Failed to create deployment token:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
